// Includes most logic for the CodeMirror editor.
// Pretty simple abstraction but it allows for a simpler interface to index.ts

import CodeMirror from 'codemirror'
// Neccesary for CodeMirror 5 to work with parcel
import '../../../../node_modules/codemirror/lib/codemirror.css'
import '../../../../node_modules/codemirror/theme/oceanic-next.css'
import 'codemirror'
import 'codemirror/mode/python/python.js'
import 'codemirror/mode/clike/clike.js'
import { Visualizer } from '../representation'
import { TreeSitterLanguage } from '../representations/tree-sitter-language'
import { assert } from 'console'

export class Editor extends Visualizer {
    private editor: CodeMirror.Editor;
    private currentLanguage: TreeSitterLanguage
    currentMark: CodeMirror.TextMarker<CodeMirror.MarkerRange>

    /**
     * Create a new editor and place it in the DOM
     * @param parentNode will be inserted as a child to this DOM element
     * @param language programming language to use. (Must be initialized!)
    */
    constructor(parentNode: HTMLElement, language: TreeSitterLanguage) {
        super();
        this.editor = CodeMirror(parentNode, {
            mode: language.name,
            lineNumbers: true,
            indentUnit: 4,          // How many spaces to indent by
            tabSize: 4,             // Number of spaces in a tab
            theme: 'oceanic-next',
            indentWithTabs: true,   // Automatically replace spaces with tabs when possible
        });
        this.setLanguage(language);
        // Take up 100% of the container
        this.editor.getWrapperElement().style.width = "100%";
        this.editor.getWrapperElement().style.height = "100%";
        // listen for changes, then push.
        this.onUserTextChange((currentCode) => {
            this.currentRootInterpretable = this.currentLanguage.fromRepresentation(currentCode);
            this.pushASTChange(this.currentRootInterpretable);
        });
    }

    public setRootNode(rootNode: Interpretable) {
        this.currentRootInterpretable = rootNode;
        this.setText(this.currentLanguage.toRepresentation(rootNode));
    }

    /**
     * Set the language to use, will change highlighting and the AST tree.
     * @param language Tree sitter language (must be initialized!)
     */
    public setLanguage(language: TreeSitterLanguage) {
        this.currentLanguage = language;
        this.setLanguageHighlights(language.name);
        // may be undefined if it has not been set yet.
        if (this.currentRootInterpretable) {
            this.setText(language.toRepresentation(this.currentRootInterpretable));
        }
    }

    protected setText(text: string) {
        this.editor.setValue(text)
    }

    protected getText(): string {
        return this.editor.getValue()
    }
    /**
     * Update the editor language syntax highlighting.
     * @param language either python or java.
     */
    protected setLanguageHighlights(languageName: 'python' | 'java') {
        this.editor.setOption('mode', languageName)
        // neccesary for CodeMirror to process this.
        // see https://codemirror.net/5/mode/clike/index.html
        if (languageName == 'java') {
            this.editor.setOption('mode', 'text/x-java');
        }
    }

    /**
     * Call a callback function whenever the user updates the text.
     * Will not call a function when using setText.
     * @param callback a function receiving the new text.
     */
    protected onUserTextChange(callback: (current_text: string) => void) {
        // CodeMirror has different change reasons, I'm trying to only do it when the user changed the text.
        // If it's in neither, it will throw an assertion error (in case I missd an edit reason)
        const user_created_should_update = ["+input", "+delete", "-delete", "cut", "paste", "copy", "drag", "undo", "redo"]
        const machine_created_should_not_update = ["setValue"]
        this.editor.on('change', (editor, changeObj) => {
            if (changeObj.origin == undefined) {
                throw new Error("changeObj should not be undefined")
            }
            // Only update the editor when user input is made, not computer input:
            if (user_created_should_update.includes(changeObj.origin)) {
                callback(editor.getValue());
                return;
            }
            // Don't update machine input. This is made in case I didn't find all the
            // user events, there doesn't seem to be great documentation on it. So if you end up
            // changing the editor's text in a way I didn't anticipate, you're notified.
            if (!machine_created_should_not_update.includes(changeObj.origin)) {
                console.error(`CodeMirror editor updated with unknown origin ${changeObj.origin}, please categorize it as user-generated or computer-generated to avoid unintentional callbacks.`)
            }
        });
    }


    markText(interpretableUuid: string) {
        if(this.currentMark) {
            this.currentMark.clear();
        }
        const textRange = this.currentLanguage.textRange.get(interpretableUuid);
        console.log(textRange, this.currentLanguage.interpretables.get(interpretableUuid));
        assert(textRange, `interpretable has no text range`)
        this.currentMark = this.editor.markText(textRange.from, textRange.to, {
            css: "background-color: goldenrod"
        });
    }
}